import { ProductType } from "@repo/api";
import { type SupportedContractType, useContractSelection } from "@repo/logic";
import { DisplayableError, NightRunBanner, cs, useIsDesktop } from "@repo/ui";
import { type FC, useEffect, useMemo } from "react";
import { LoggedInBar } from "../../components/AppBar/Bars";
import { BottomNavigationDesktopComponent } from "../../components/BottomNavigation";
import { Contract, contractByIdFromType } from "../../components/Contract";
import ContractCard, { ContractCardSkeleton } from "../../components/Contract/ContractCard";
import {
    ContractHeaderMobileCarrets,
    ContractHeaderWithFufiData,
} from "../../components/Contract/ContractCard/ContractCardHeader";
import {
    ContractCardDesktopMenu,
    ContractCardDesktopMenuWithFufiData,
} from "../../components/Contract/ContractCardDesktopMenu";
import { ErrorBoundaryWithAsyncContext, useErrorContext } from "../../components/ErrorBoundary";
import { DefaultErrorUIWithCard } from "../../components/ErrorBoundary/DefaultErrorUi";
import { ScrollSnapper } from "../../components/ScrollSnapper";
import { Seo } from "../../components/SEO";
import { TransactionSkeleton } from "../../components/Transactions/SingleTransactionRow";
import Version from "../../components/Version";
import { ContractIDProvider } from "../../context/ContractIDContext";
import { ContractTypeProvider } from "../../context/ContractTypeContext";
import withProtection from "../../highOrderComponents/withProtection";
import { useContracts } from "../../queryHooks";
import * as Styled from "../../components/Contract/ContractsPage.styled";

export const HeadingMainLengthWithData: FC = () => {
    const { data: contracts, isSuccess } = useContracts();

    if (!isSuccess) return <>Načítáme Vaše smlouvy...</>;

    if (contracts.length === 1) return <>Vaše {contracts.length} aktivní smlouva</>;
    else if (contracts.length < 5) return <>Vaše {contracts.length} aktivní smlouvy</>;
    return <>Vašich {contracts.length} aktivních smluv</>;
};

type IContractCardsDesktopMenu = {
    setContractIndex: (i: number) => void;
    contractIndex: number;
    errorCallbacks: (() => void)[];
};

const ContractDesktopSelectorListWithData: FC<IContractCardsDesktopMenu> = ({
    contractIndex,
    setContractIndex,
    errorCallbacks,
}) => {
    const { data: contracts, isLoading } = useContracts();

    if (isLoading)
        return (
            <>
                <TransactionSkeleton />
                <TransactionSkeleton />
            </>
        );

    return (
        <>
            {contracts?.map((contract, i) => {
                return (
                    <ContractTypeProvider key={contract.id} contractType={contract.type as SupportedContractType}>
                        {contract.type === ProductType.Clf ? (
                            <ContractCardDesktopMenuWithFufiData
                                data={contract}
                                contractId={contract.id}
                                isActive={contractIndex === i}
                                setContractIndex={() => {
                                    setContractIndex(i);
                                }}
                                onDetailError={errorCallbacks[i]}
                            />
                        ) : (
                            <ContractCardDesktopMenu
                                data={contract}
                                setContractIndex={() => {
                                    setContractIndex(i);
                                }}
                                contractIndex={contractIndex}
                                selectedProduct={i}
                                onDetailError={errorCallbacks[i]}
                            />
                        )}
                    </ContractTypeProvider>
                );
            })}
        </>
    );
};

type ICustomWidthSwipeableViews = {
    setContractIndex: (i: number) => void;
    contractIndex: number;
    erroredIndices: Set<number>;
};

const ContractMobileSwipableSelectorWithData: FC<ICustomWidthSwipeableViews> = ({
    contractIndex,
    setContractIndex,
    erroredIndices,
}) => {
    const { data: contracts, isLoading } = useContracts();

    if (isLoading)
        return (
            <Styled.MobileLoadingCardsRow>
                <ContractCardSkeleton width={0.7} />
                <ContractCardSkeleton width={0.3} />
            </Styled.MobileLoadingCardsRow>
        );

    // TODO refactor carret usage into a singular wrapper for all types of cards and
    // fix carret discrepancy
    return (
        <ScrollSnapper
            index={contractIndex}
            onIndexChange={setContractIndex}
            sx={Styled.mobileContractsSnapperSx}
            childContainerProps={Styled.mobileContractsChildContainerProps}
        >
            {contracts?.map(({ id, type, proposalNumber }, i) => (
                <ContractTypeProvider key={id} contractType={type as SupportedContractType}>
                    <ContractHeaderMobileCarrets
                        isFirst={i === 0}
                        isLast={i === contracts.length - 1}
                        onShift={(modifier) => {
                            setContractIndex(i + modifier);
                        }}
                    >
                        <Styled.MobileContractStateBox isErrored={erroredIndices.has(i)}>
                            {type === ProductType.Clf ? (
                                <Styled.FufiMobileHeaderPaper elevation={4}>
                                    <ContractHeaderWithFufiData contractId={id} proposalNumber={proposalNumber} />
                                </Styled.FufiMobileHeaderPaper>
                            ) : (
                                <ContractCard contractId={id} type={type} />
                            )}
                        </Styled.MobileContractStateBox>
                    </ContractHeaderMobileCarrets>
                </ContractTypeProvider>
            ))}
        </ScrollSnapper>
    );
};

const ContractsList: FC = () => {
    const { data } = useContracts();
    const contracts = useMemo(() => data ?? [], [data]);
    const {
        selectedIndex: contractIndex,
        setSelectedIndex: setContractIndex,
        errorCallbacks,
        erroredIndices,
        allErrored,
    } = useContractSelection(contracts);
    const isDesktop = useIsDesktop();
    const { setError } = useErrorContext();

    useEffect(() => {
        if (allErrored) {
            setError(
                new DisplayableError(cs.errors.general, {
                    title: cs.errors.somethingWentWrong,
                }),
            );
        }
    }, [allErrored, setError]);

    return (
        <Styled.ContractsListLayout>
            <Styled.ContractsSelectorColumn spacing={6}>
                {isDesktop ? ( // don't swipe navigation cards on desktop
                    <ContractDesktopSelectorListWithData
                        setContractIndex={setContractIndex}
                        contractIndex={contractIndex}
                        errorCallbacks={errorCallbacks}
                    />
                ) : (
                    <ContractMobileSwipableSelectorWithData
                        setContractIndex={setContractIndex}
                        contractIndex={contractIndex}
                        erroredIndices={erroredIndices}
                    />
                )}
                {isDesktop && (
                    <>
                        <NightRunBanner showBackgroundImage /> {/* Night run banner */}
                        <Styled.DesktopHelpBox noImage />
                    </>
                )}
            </Styled.ContractsSelectorColumn>
            <Styled.ContractsDetailColumn>
                <ScrollSnapper
                    index={contractIndex}
                    onIndexChange={setContractIndex}
                    sx={Styled.detailSnapperSx}
                    childContainerProps={Styled.detailChildContainerProps}
                >
                    {data ? (
                        data.map(({ type, id, proposalNumber }, i) => (
                            <ErrorBoundaryWithAsyncContext key={id} fallbackUI={DefaultErrorUIWithCard}>
                                <ContractIDProvider contractId={id} proposalNumber={proposalNumber}>
                                    <ContractTypeProvider key={id} contractType={type as SupportedContractType}>
                                        {(() => {
                                            const ContractComponent =
                                                contractByIdFromType[type as keyof typeof contractByIdFromType];
                                            return (
                                                <ContractComponent
                                                    contractId={id}
                                                    proposalNumber={proposalNumber}
                                                    onDetailError={errorCallbacks[i]}
                                                />
                                            );
                                        })()}
                                    </ContractTypeProvider>
                                </ContractIDProvider>
                            </ErrorBoundaryWithAsyncContext>
                        ))
                    ) : (
                        <Contract isSuccess={false} />
                    )}
                </ScrollSnapper>
            </Styled.ContractsDetailColumn>
        </Styled.ContractsListLayout>
    );
};

export const AppBar: FC = () => <LoggedInBar BottomNavigationComponent={BottomNavigationDesktopComponent} />;

const DashboardPage: FC = () => {
    const isD = useIsDesktop();

    if (isD)
        return (
            <Styled.DesktopDashboardLayout>
                <Styled.DesktopPageTitle>
                    <HeadingMainLengthWithData />
                </Styled.DesktopPageTitle>
                <ContractsList />
                <Version />
            </Styled.DesktopDashboardLayout>
        );

    return (
        <Styled.MobileDashboardLayout>
            <Styled.MobileDashboardContent spacing={{ xs: 5, md: 7 }}>
                <Styled.MobilePageTitle>
                    <HeadingMainLengthWithData />
                </Styled.MobilePageTitle>
                <ContractsList />
                <Styled.MobileNightRunWrapper>
                    <NightRunBanner showBackgroundImage /> {/* Night run banner */}
                </Styled.MobileNightRunWrapper>
            </Styled.MobileDashboardContent>
            <Version />
        </Styled.MobileDashboardLayout>
    );
};

export default withProtection(DashboardPage);

export const Head: FC = () => (
    <Seo title="Smlouvy">
        <meta name="robots" content="noindex" />
    </Seo>
);
